import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import API from '../../services/api'
import { debounce } from '../../utils/Debounce'
import Paginator from '../../shared/Paginator'
import TableLoader from '../../shared/TableLoader'
import NoDataFound from '../../shared/NoDataFound'
import { Toast } from '../../shared/Toast'
import { SlInput, SlIcon, SlButton } from '@shoelace-style/shoelace/dist/react'
import './DistrictWiseDaus.scss'

const LIMIT = 20
const ALL_OPTION = { value: '', label: 'All' }

const selectStyles = {
    control: (base) => ({ ...base, minHeight: 38, borderRadius: 8, borderColor: '#e2e8f0' }),
    menu: (base) => ({ ...base, fontSize: 14, zIndex: 60 }),
    menuPortal: (base) => ({ ...base, zIndex: 60 }),
}

const TABS = [
    { key: 'language', label: 'Language Wise' },
    { key: 'state', label: 'State Wise' },
    { key: 'city', label: 'City Wise' },
]

const safeFetch = async (url, data) => {
    try {
        const response = await API({ url, method: 'POST', data })
        return response.code === 200 ? (response.data || []) : []
    } catch (e) {
        return []
    }
}

const fetchLanguages = async () =>
    (await safeFetch('/district-wise-daus/get-languages', {})).map((l) => ({ value: l, label: l }))

const fetchStates = async (language) =>
    (await safeFetch('/district-wise-daus/get-states', { language })).map((s) => ({ value: s, label: s }))

const fetchCities = async (language, state) =>
    (await safeFetch('/district-wise-daus/get-cities', { language, state })).map((c) => ({ value: c, label: c }))

function DistrictWiseDaus() {
    const [activeTab, setActiveTab] = useState('language')

    return (
        <div className="district-wise-daus-page flex h-[calc(100vh-72px)] flex-col p-6 font-sans">
            <div className="shrink-0">
                <h4 className="mb-0 text-2xl font-semibold text-slate-800">District Wise MAUs</h4>
                <p className="mt-1.5 mb-4 text-sm text-slate-500">MAU by language, state and city - loaded from the source sheet, editable at the city level.</p>
                <div className="flex gap-1 border-b border-slate-200">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === tab.key ? 'border-sky-500 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {activeTab === 'language' && <LanguageWiseTab />}
            {activeTab === 'state' && <StateWiseTab />}
            {activeTab === 'city' && <CityWiseTab />}
        </div>
    );
}

function LanguageWiseTab() {
    const [rows, setRows] = useState([])
    const [loader, setLoader] = useState(false)
    const [search, setSearch] = useState('')

    const getData = async () => {
        setLoader(true)
        const data = await safeFetch('/district-wise-daus/get-language-summary', { search })
        setRows(data)
        setLoader(false)
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    return (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <SlInput
                size="medium"
                className="w-72 shrink-0"
                onSlInput={debounce((e) => setSearch(e.target.value), 500)}
                placeholder="Search by language…"
            >
                <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
            </SlInput>

            <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="district-wise-daus-table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Language</th>
                            <th>MAU</th>
                        </tr>
                    </thead>
                    {loader ? <TableLoader colspan={2} /> : rows?.length > 0 ? (
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.language}>
                                    <td className="text-slate-700">{row.language}</td>
                                    <td className="text-slate-700">{Number(row.mau).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    ) : <NoDataFound colspan={2} />}
                </table>
            </div>
        </div>
    )
}

function StateWiseTab() {
    const [rows, setRows] = useState([])
    const [loader, setLoader] = useState(false)
    const [search, setSearch] = useState('')
    const [langFilter, setLangFilter] = useState(null)
    const [langOptions, setLangOptions] = useState([])

    useEffect(() => {
        fetchLanguages().then(setLangOptions)
    }, [])

    const getData = async () => {
        setLoader(true)
        const data = await safeFetch('/district-wise-daus/get-state-summary', { search, ...(langFilter && { language: langFilter.value }) })
        setRows(data)
        setLoader(false)
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, langFilter])

    return (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-4 shrink-0">
                <Select
                    className="w-48"
                    styles={selectStyles}
                    options={[ALL_OPTION, ...langOptions]}
                    value={langFilter || ALL_OPTION}
                    onChange={(o) => setLangFilter(o?.value ? o : null)}
                    placeholder="Language"
                />
                <SlInput
                    size="medium"
                    className="w-72"
                    onSlInput={debounce((e) => setSearch(e.target.value), 500)}
                    placeholder="Search by language or state…"
                >
                    <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
                </SlInput>
            </div>

            <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="district-wise-daus-table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Language</th>
                            <th>State</th>
                            <th>MAU</th>
                        </tr>
                    </thead>
                    {loader ? <TableLoader colspan={3} /> : rows?.length > 0 ? (
                        <tbody>
                            {rows.map((row) => (
                                <tr key={`${row.language}_${row.state}`}>
                                    <td className="text-slate-700">{row.language}</td>
                                    <td className="text-slate-700">{row.state}</td>
                                    <td className="text-slate-700">{Number(row.mau).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    ) : <NoDataFound colspan={3} />}
                </table>
            </div>
        </div>
    )
}

function CityWiseTab() {
    const [rows, setRows] = useState([])
    const [loader, setLoader] = useState(false)
    const [totalPages, setTotalPages] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [sumMau, setSumMau] = useState(null)
    const [currentPage, setCurrentPage] = useState(0)
    const [search, setSearch] = useState('')

    const [langFilter, setLangFilter] = useState(null)
    const [stateFilter, setStateFilter] = useState(null)
    const [cityFilter, setCityFilter] = useState(null)
    const [langOptions, setLangOptions] = useState([])
    const [stateOptions, setStateOptions] = useState([])
    const [cityOptions, setCityOptions] = useState([])

    const [editKey, setEditKey] = useState(null)
    const [editValue, setEditValue] = useState('')

    useEffect(() => {
        fetchLanguages().then(setLangOptions)
    }, [])

    const getData = async () => {
        setLoader(true)
        try {
            const response = await API({
                url: '/district-wise-daus/get-list',
                method: 'POST',
                data: {
                    page: currentPage,
                    limit: LIMIT,
                    search,
                    ...(langFilter && { language: langFilter.value }),
                    ...(stateFilter && { state: stateFilter.value }),
                    ...(cityFilter && { city: cityFilter.value }),
                }
            })
            if (response.code === 200) {
                setRows(response.data.data)
                setTotalCount(response.data.total)
                setSumMau(response.data.sum_mau ?? null)
                setTotalPages(Math.ceil(response.data.total / LIMIT))
            }
        } catch (e) {
            Toast('error', 'Failed to load data')
        }
        setEditKey(null)
        setEditValue('')
        setLoader(false)
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, search, langFilter, stateFilter, cityFilter])

    const langFilterChange = async (option) => {
        const value = option?.value || null
        setLangFilter(value ? option : null)
        setStateFilter(null)
        setCityFilter(null)
        setStateOptions([])
        setCityOptions([])
        setCurrentPage(0)
        if (!value) return
        setStateOptions(await fetchStates(value))
    }

    const stateFilterChange = async (option) => {
        const value = option?.value || null
        setStateFilter(value ? option : null)
        setCityFilter(null)
        setCityOptions([])
        setCurrentPage(0)
        if (!value) return
        setCityOptions(await fetchCities(langFilter.value, value))
    }

    const cityFilterChange = (option) => {
        setCityFilter(option?.value ? option : null)
        setCurrentPage(0)
    }

    const rowKey = (row) => row.id

    const startEdit = (row) => {
        setEditKey(rowKey(row))
        setEditValue(row.mau ?? '')
    }

    const cancelEdit = () => {
        setEditKey(null)
        setEditValue('')
    }

    const saveEdit = async (row) => {
        if (editValue === '' || isNaN(editValue) || Number(editValue) < 0) {
            Toast('error', 'Please enter a valid MAU value')
            return
        }
        setLoader(true)
        try {
            const response = await API({ url: '/district-wise-daus/update', method: 'POST', data: { id: row.id, mau: Number(editValue) } })
            if (response.code === 200) {
                getData()
                return
            }
        } catch (e) {
            Toast('error', 'Failed to save')
        }
        setLoader(false)
    }

    return (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-4 shrink-0">
                <Select
                    className="w-48"
                    styles={selectStyles}
                    options={[ALL_OPTION, ...langOptions]}
                    value={langFilter || ALL_OPTION}
                    onChange={langFilterChange}
                    placeholder="Language"
                />
                <Select
                    className="w-48"
                    styles={selectStyles}
                    isDisabled={!langFilter}
                    options={[ALL_OPTION, ...stateOptions]}
                    value={stateFilter || ALL_OPTION}
                    onChange={stateFilterChange}
                    placeholder="State"
                />
                <Select
                    className="w-48"
                    styles={selectStyles}
                    isDisabled={!stateFilter}
                    options={[ALL_OPTION, ...cityOptions]}
                    value={cityFilter || ALL_OPTION}
                    onChange={cityFilterChange}
                    placeholder="City"
                />
                <SlInput
                    size="medium"
                    className="w-72"
                    onSlInput={debounce((e) => setSearch(e.target.value), 500)}
                    placeholder="Search by language, state or city…"
                >
                    <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
                </SlInput>
            </div>

            <p className="mt-3 mb-0 text-sm text-slate-500 shrink-0">
                Showing <span className="font-semibold text-slate-700">{rows?.length || 0}</span> of {totalCount} rows
                {sumMau !== null && (
                    <> — Sum of MAU for this selection: <span className="font-semibold text-slate-700">{sumMau.toLocaleString()}</span></>
                )}
            </p>

            <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="district-wise-daus-table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Language</th>
                            <th>State</th>
                            <th>City</th>
                            <th>MAU</th>
                        </tr>
                    </thead>
                    {loader ? <TableLoader colspan={4} /> : rows?.length > 0 ? (
                        <tbody>
                            {rows.map((row) => (
                                <tr key={rowKey(row)}>
                                    <td className="text-slate-700">{row.language}</td>
                                    <td className="text-slate-700">{row.state}</td>
                                    <td className="text-slate-700">{row.city}</td>
                                    <td>
                                        {editKey === rowKey(row) ? (
                                            <div className="flex items-center gap-2 py-1">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="w-28 rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    autoFocus
                                                />
                                                <SlButton variant="primary" size="small" onClick={() => saveEdit(row)}>Save</SlButton>
                                                <SlButton variant="default" size="small" onClick={cancelEdit}>Cancel</SlButton>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="text-slate-700">{row.mau ?? '—'}</span>
                                                <sl-tooltip content="Edit">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(row)}
                                                        aria-label="Edit"
                                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-sky-400 hover:text-sky-500"
                                                    >
                                                        <SlIcon library="lucide" name="pencil"></SlIcon>
                                                    </button>
                                                </sl-tooltip>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : <NoDataFound colspan={4} />}
                </table>
            </div>

            <div className="mt-4 flex shrink-0 justify-end">
                {totalPages > 1 && (
                    <Paginator
                        pageCount={totalPages}
                        handlePageClick={(e) => setCurrentPage(e.selected)}
                        forcePage={currentPage}
                    />
                )}
            </div>
        </div>
    )
}

export default DistrictWiseDaus;
